// src/components/conversations/ConversationsInbox.tsx
//
// Bandeja de Conversaciones v1 (rediseño 2026-10-22, decisión de Samuel).
// Cada conversación responde tres preguntas:
//   ¿qué agente?   pestaña  Clientes (agente comercial) · Equipo (agente interno) · Pruebas
//   ¿qué canal?    chip     Panel · Sitio · WhatsApp · Messenger · Instagram · Voz · Teléfono · Correo
//   ¿quién?        fila     lead/cliente del CRM o usuario del equipo (o el teléfono si no hay)
//
// 🔒 La privacidad la decide ORIÓN (GET /threads/inbox): las conversaciones del
// equipo son privadas por usuario y solo admin/cto las ven aquí (auditoría).
// Esta vista solo refleja lo que el servidor devolvió: la pestaña «Equipo» se
// muestra si `viewer.can_see_team`.
//
// Compatibilidad: si el backend no tiene /threads/inbox, `onUnavailable()` y
// ThreadsDesk vuelve a la bandeja anterior.
//
// Colores: SOLO tokens semánticos (bg-card, text-foreground, border-border…) y
// tonos con variante dark: — se ve bien en el shell Orion y en el clásico.

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Bot, Cpu, ExternalLink, Facebook, Globe, HelpCircle, Instagram, LayoutDashboard, Mail,
  MessageCircle, MessageSquare, Mic, Phone, RefreshCw, Search, ShieldCheck, X,
} from "lucide-react";

import { getConversations, getInbox, type InboxItem, type InboxParty, type InboxResponse } from "@/lib/api";
import { useModules } from "@/context/ModulesContext";
import { AgentControl } from "@/components/AgentControl";
import { WhatsAppGlyph } from "@/modules/whatsapp-app/components/WhatsAppGlyph";
import {
  ChatSheet, derivePriority, extractAssistantPreview, mapDto, mxStamp, toBloques,
  type ConversationLog, type Thread,
} from "@/components/ThreadsDesk";

/* ───────── Canal ───────── */
type Tone = { cls: string; Icon: React.ComponentType<{ className?: string }>; label: string };
// Texto -700 en claro / -300 en oscuro sobre fondo tenue: AA (≥4.5:1) en ambos.
export const INBOX_CHANNELS: Record<string, Tone> = {
  panel:          { label: "Panel",     Icon: LayoutDashboard, cls: "text-indigo-700 dark:text-indigo-300 bg-indigo-500/10 border-indigo-500/30" },
  web:            { label: "Sitio",     Icon: Globe,           cls: "text-sky-800 dark:text-sky-300 bg-sky-500/10 border-sky-500/30" },
  whatsapp:       { label: "WhatsApp",  Icon: MessageCircle,   cls: "text-emerald-800 dark:text-emerald-300 bg-emerald-500/10 border-emerald-500/30" },
  facebook:       { label: "Messenger", Icon: Facebook,        cls: "text-blue-700 dark:text-blue-300 bg-blue-500/10 border-blue-500/30" },
  instagram:      { label: "Instagram", Icon: Instagram,       cls: "text-pink-700 dark:text-pink-300 bg-pink-500/10 border-pink-500/30" },
  voice_realtime: { label: "Voz",       Icon: Mic,             cls: "text-violet-700 dark:text-violet-300 bg-violet-500/10 border-violet-500/30" },
  voice_phone:    { label: "Teléfono",  Icon: Phone,           cls: "text-violet-700 dark:text-violet-300 bg-violet-500/10 border-violet-500/30" },
  email:          { label: "Correo",    Icon: Mail,            cls: "text-amber-800 dark:text-amber-300 bg-amber-500/10 border-amber-500/30" },
  api:            { label: "Sistema",   Icon: Cpu,             cls: "text-muted-foreground bg-muted border-border" },
  unknown:        { label: "Otro",      Icon: HelpCircle,      cls: "text-muted-foreground bg-muted border-border" },
};
const CHANNEL_ORDER = ["panel", "web", "whatsapp", "facebook", "instagram", "voice_realtime", "voice_phone", "email", "api", "unknown"];
const chMeta = (c?: string | null) => INBOX_CHANNELS[c || ""] || INBOX_CHANNELS.unknown;
// Canales donde «Tomar conversación» tiene sentido (el humano contesta por ahí).
const TAKEOVER_CHANNELS = new Set(["whatsapp", "facebook", "instagram", "voice_phone", "email"]);

const STAGE_LABEL: Record<string, string> = {
  lead: "Lead", prospecto: "Prospecto", cliente: "Cliente", unsubscribed: "Baja",
  "admin-linked": "Equipo",
};

type Tab = InboxParty;
type Cat = "all" | "urgente" | "revision" | "neutra";

const TAB_COPY: Record<Tab, { label: string; hint: string }> = {
  external: { label: "Clientes", hint: "Leads y clientes con el agente comercial." },
  internal: { label: "Equipo", hint: "Tu equipo con el agente interno. Privadas: solo administración las ve, para auditoría." },
  test: { label: "Pruebas", hint: "Diagnósticos, sondas y demos. No cuentan como clientes." },
};

function displayName(it: InboxItem): string {
  const n = it.person?.name?.trim();
  if (n) return n;
  const tel = String(it.telefono || "");
  if (/^public-/i.test(tel) || !tel || /^(\d)\1{6,}$/.test(tel)) {
    return it.party === "internal" ? "Integrante del equipo" : "Visitante del sitio";
  }
  return tel;
}

function initials(s: string): string {
  const parts = s.replace(/[^\p{L}\p{N} ]/gu, " ").trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "·";
  if (/^\d+$/.test(parts[0])) return parts[0].slice(-2);
  return (parts[0][0] + (parts[1]?.[0] || "")).toUpperCase();
}

const phoneForSearch = (tel?: string | null) => {
  const d = String(tel || "").replace(/\D/g, "");
  return d.length >= 10 ? d.slice(-10) : "";
};

/* ───────── Componente ───────── */
export default function ConversationsInbox({
  onUnavailable,
  fetchInbox = getInbox,
  initialTab = "external",
}: {
  onUnavailable: () => void;
  /** Inyectable para la vista previa de desarrollo (datos simulados). */
  fetchInbox?: typeof getInbox;
  initialTab?: InboxParty;
}) {
  const unavailableRef = useRef(onUnavailable);
  unavailableRef.current = onUnavailable;
  const navigate = useNavigate();
  const { isEnabled, isAdmin, userRoles } = useModules();
  const esVendedora = !isAdmin && !!userRoles?.some((r) => ["vendedor", "socio", "partner"].includes(r))
    && !userRoles?.some((r) => ["cto", "developer"].includes(r));
  const puedeVerLeads = (() => { try { return isEnabled("crm"); } catch { return false; } })();
  // 📱 WhatsApp tiene su propia app (módulo whatsapp_app, solo con número conectado):
  // sus chats de CLIENTES viven allá; aquí quedan sitio, Messenger, Instagram, voz…
  const waApp = (() => { try { return isEnabled("whatsapp_app"); } catch { return false; } })() && !esVendedora;

  // 🛎️ Enlace desde la campana: ?hilo=<thread_id>&tel=<teléfono> abre esa conversación.
  // Se busca por teléfono e incluye WhatsApp aunque viva en su propia app.
  const [sp, setSp] = useSearchParams();
  const [hiloPedido, setHiloPedido] = useState<string | null>(() => sp.get("hilo"));
  const [tab, setTab] = useState<Tab>(initialTab);
  const [canal, setCanal] = useState<string>("all");
  const [cat, setCat] = useState<Cat>("all");
  const [q, setQ] = useState(() => (sp.get("hilo") ? (sp.get("tel") || "").replace(/\D/g, "").slice(-10) : ""));
  const [qDeb, setQDeb] = useState(() => (sp.get("hilo") ? (sp.get("tel") || "").replace(/\D/g, "").slice(-10) : ""));
  useEffect(() => { const id = setTimeout(() => setQDeb(q.trim()), 350); return () => clearTimeout(id); }, [q]);

  const [data, setData] = useState<InboxResponse | null>(null);
  const [status, setStatus] = useState<"loading" | "error" | "success">("loading");
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    const ac = new AbortController();
    setStatus("loading");
    setError(null);
    fetchInbox({ party: tab, channel: canal === "all" ? undefined : canal, search: qDeb || undefined, limit: 200,
      excludeChannel: waApp && tab === "external" && !hiloPedido ? "whatsapp" : undefined }, { signal: ac.signal })
      .then((r) => {
        if (r === null) { unavailableRef.current(); return; }
        setData(r);
        setStatus("success");
        if (tab === "internal" && !r.viewer.can_see_team && !(r.counts.party.internal > 0)) setTab("external");
      })
      .catch((e: any) => {
        if (e?.name === "AbortError") return;
        setStatus("error");
        setError(e?.message || "No se pudo cargar la bandeja");
      });
    return () => ac.abort();
  }, [tab, canal, qDeb, nonce, fetchInbox, waApp, hiloPedido]);

  const items = data?.items || [];
  const prio = useCallback((it: InboxItem) => derivePriority(it as any), []);
  const visible = useMemo(() => {
    if (cat === "all") return items;
    return items.filter((it) => {
      const p = prio(it);
      return (cat === "urgente" && p === "urgent") || (cat === "revision" && p === "atender") || (cat === "neutra" && p === "neutral");
    });
  }, [items, cat, prio]);
  const catCounts = useMemo(() => {
    const u = items.filter((x) => prio(x) === "urgent").length;
    const r = items.filter((x) => prio(x) === "atender").length;
    return { all: items.length, u, r, n: items.length - u - r };
  }, [items, prio]);

  const counts = data?.counts;
  const canSeeTeam = !!data?.viewer.can_see_team;
  const tabs: Tab[] = canSeeTeam ? ["external", "internal", "test"] : ["external", "test"];
  const channelChips = useMemo(() => {
    const m = counts?.channel || {};
    return CHANNEL_ORDER.filter((c) => (m[c] || 0) > 0 || c === canal).map((c) => ({ c, n: m[c] || 0 }));
  }, [counts, canal]);
  const totalInTab = Object.values(counts?.channel || {}).reduce((a, b) => a + b, 0);

  /* Conversación abierta */
  const [active, setActive] = useState<InboxItem | null>(null);
  const [msgs, setMsgs] = useState<ConversationLog[]>([]);
  const [msgsStatus, setMsgsStatus] = useState<"idle" | "loading" | "success">("idle");
  useEffect(() => {
    let cancelled = false;
    if (!active) return;
    setMsgsStatus("loading");
    getConversations(undefined, active.session_id)
      .then((rows: any[]) => {
        if (cancelled) return;
        const sysTypes = ["texto", "imagen", "video", "audio", "pdf"];
        setMsgs((rows || []).map((r: any) => {
          const raw = String(r.message ?? r.text ?? "");
          const bloques = toBloques(raw).filter((b) => sysTypes.includes(b.tipo) && !b.contenido.includes("[INTERNAL]"));
          return {
            role: r.role === "assistant" ? "assistant" : "user",
            message: bloques.length ? "" : raw,
            timestamp: r.timestamp ?? r.at ?? r.created_at ?? Date.now(),
            bloques, telefono: r.telefono,
          } as ConversationLog;
        }));
      })
      .catch(() => { if (!cancelled) setMsgs([]); })
      .finally(() => { if (!cancelled) setMsgsStatus("success"); });
    return () => { cancelled = true; };
  }, [active]);
  useEffect(() => {
    if (!hiloPedido || status !== "success" || !data) return;
    const it = data.items.find((x) => x.id === hiloPedido);
    if (it) setActive(it);
    setHiloPedido(null);
    const next = new URLSearchParams(sp);
    next.delete("hilo");
    next.delete("tel");
    setSp(next, { replace: true });
  }, [hiloPedido, status, data, sp, setSp]);
  const activeThread: Thread | null = active ? mapDto(active as any) : null;

  const tabBtn = (t: Tab) => {
    const sel = tab === t;
    const n = counts?.party?.[t];
    return (
      <button
        key={t}
        role="tab"
        aria-selected={sel}
        onClick={() => { setTab(t); setCanal("all"); setCat("all"); }}
        className={[
          "relative px-3 sm:px-4 py-2 text-[14px] font-medium rounded-lg transition-colors inline-flex items-center gap-1.5",
          sel ? "bg-card text-foreground shadow-sm border border-border" : "text-muted-foreground hover:text-foreground",
        ].join(" ")}
      >
        {t === "internal" && <ShieldCheck className="w-4 h-4" aria-hidden />}
        {TAB_COPY[t].label}
        {typeof n === "number" && (
          <span className={["text-[12px] tabular-nums rounded-full px-1.5", sel ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"].join(" ")}>{n}</span>
        )}
      </button>
    );
  };

  return (
    <div className="w-full max-w-5xl mx-auto flex flex-col min-h-0 text-foreground">
      {/* Encabezado */}
      <div className="px-3 sm:px-4 pt-3 pb-2 flex items-center gap-2">
        <div className="p-2 rounded-xl bg-muted border border-border"><MessageSquare className="w-5 h-5" aria-hidden /></div>
        <h1 className="font-semibold text-[17px] sm:text-lg">{esVendedora ? "Mis conversaciones asignadas" : "Conversaciones"}</h1>
        <button className="ml-auto o-icon-btn p-2 rounded-lg border border-border hover:bg-muted" title="Refrescar" aria-label="Refrescar" onClick={() => setNonce((x) => x + 1)}>
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      {/* Pestañas: qué agente */}
      <div className="px-3 sm:px-4">
        <div role="tablist" aria-label="Tipo de conversación" className="inline-flex gap-1 p-1 rounded-xl bg-muted border border-border max-w-full overflow-x-auto">
          {tabs.map(tabBtn)}
        </div>
        <p className="mt-1.5 text-[12px] text-muted-foreground">{TAB_COPY[tab].hint}</p>
      </div>

      {waApp && tab === "external" && (
        <div className="px-3 sm:px-4 pt-2">
          <a
            href="/m/whatsapp-app/chats"
            onClick={(e) => { e.preventDefault(); navigate("/m/whatsapp-app/chats"); }}
            className="flex items-center gap-2.5 rounded-xl border border-border bg-card px-3 py-2 text-[13px] hover:bg-muted"
          >
            <WhatsAppGlyph size={18} title={null} />
            <span className="flex-1">Los chats de WhatsApp de tus clientes están en su app.</span>
            <span className="font-medium inline-flex items-center gap-1">Abrir WhatsApp <ExternalLink className="w-3.5 h-3.5" aria-hidden /></span>
          </a>
        </div>
      )}

      {/* Búsqueda */}
      <div className="px-3 sm:px-4 pt-2">
        <label className="flex items-center gap-2 px-3 py-2 rounded-xl bg-card border border-border focus-within:ring-2 focus-within:ring-primary/40">
          <Search className="w-4 h-4 text-muted-foreground" aria-hidden />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar nombre, teléfono o texto…"
            className="bg-transparent w-full outline-none placeholder:text-muted-foreground text-[15px]"
            aria-label="Buscar conversaciones"
          />
          {q && <button onClick={() => setQ("")} aria-label="Limpiar búsqueda" className="text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /></button>}
        </label>
      </div>

      {/* Chips: canal (con conteo) */}
      <div className="px-3 sm:px-4 pt-2 overflow-x-auto no-scrollbar">
        <div className="flex gap-1.5 min-w-max" role="group" aria-label="Filtrar por canal">
          <button
            onClick={() => setCanal("all")}
            aria-pressed={canal === "all"}
            className={`text-[13px] px-2.5 py-1 rounded-full border ${canal === "all" ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:text-foreground"}`}
          >
            Todos <span className="tabular-nums">({totalInTab})</span>
          </button>
          {channelChips.map(({ c, n }) => {
            const m = chMeta(c);
            const sel = canal === c;
            return (
              <button
                key={c}
                onClick={() => setCanal(sel ? "all" : c)}
                aria-pressed={sel}
                className={`text-[13px] px-2.5 py-1 rounded-full border inline-flex items-center gap-1 ${sel ? m.cls + " ring-2 ring-primary/30" : "border-border text-muted-foreground hover:text-foreground"}`}
              >
                <m.Icon className="w-3.5 h-3.5" aria-hidden /> {m.label} <span className="tabular-nums">({n})</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Chips: urgencia (los mismos de siempre) */}
      <div className="px-3 sm:px-4 pt-2 pb-2 overflow-x-auto no-scrollbar">
        <div className="flex gap-1.5 min-w-max" role="group" aria-label="Filtrar por urgencia">
          {([
            ["all", `Todas (${catCounts.all})`, "border-border text-muted-foreground"],
            ["urgente", `Urgentes (${catCounts.u})`, "border-rose-500/40 text-rose-700 dark:text-rose-300"],
            ["revision", `Revisión (${catCounts.r})`, "border-amber-500/40 text-amber-800 dark:text-amber-300"],
            ["neutra", `Neutras (${catCounts.n})`, "border-border text-muted-foreground"],
          ] as [Cat, string, string][]).map(([k, label, cls]) => (
            <button key={k} onClick={() => setCat(k)} aria-pressed={cat === k}
              className={`text-[13px] px-2.5 py-1 rounded-lg border ${cls} ${cat === k ? "bg-muted ring-2 ring-primary/30 text-foreground" : "hover:bg-muted/60"}`}>
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Lista */}
      <div className="flex-1 min-h-0 px-3 sm:px-4 pb-[calc(16px+env(safe-area-inset-bottom))]">
        {status === "loading" && (
          <div className="space-y-2 pt-1" aria-busy="true">
            {Array.from({ length: 6 }).map((_, i) => <div key={i} className="h-[88px] rounded-2xl bg-muted/60 border border-border animate-pulse" />)}
          </div>
        )}
        {status === "error" && (
          <div role="alert" className="rounded-xl border border-destructive/40 bg-destructive/10 text-destructive p-3 text-sm">{error}</div>
        )}
        {status === "success" && visible.length === 0 && (
          <div className="rounded-2xl border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
            {esVendedora ? "Aún no tienes conversaciones asignadas." : "No hay conversaciones con estos filtros."}
          </div>
        )}
        {status === "success" && visible.length > 0 && (
          <ul className="space-y-2 pt-1">
            {visible.map((it) => (
              <InboxRow
                key={it.id}
                it={it}
                urgent={prio(it) === "urgent"}
                onOpen={() => setActive(it)}
                leadHref={puedeVerLeads && it.party !== "internal" && it.party !== "test"
                  ? (() => {
                      const term = it.person?.name || phoneForSearch(it.telefono);
                      return term ? `/m/crm/leads?buscar=${encodeURIComponent(term)}` : null;
                    })()
                  : null}
                onNavigate={(href) => navigate(href)}
              />
            ))}
          </ul>
        )}
      </div>

      <ChatSheet
        open={!!active}
        onClose={() => setActive(null)}
        thread={activeThread}
        title={active ? displayName(active) : null}
        messages={msgs}
        status={msgsStatus}
        readOnly={!!active && (active.party === "internal" || active.channel === "panel")}
      />
    </div>
  );
}

function InboxRow({
  it, urgent, onOpen, leadHref, onNavigate,
}: {
  it: InboxItem;
  urgent: boolean;
  onOpen: () => void;
  leadHref: string | null;
  onNavigate: (href: string) => void;
}) {
  const ch = chMeta(it.channel);
  const name = displayName(it);
  const preview = extractAssistantPreview(it.last_message_preview || "");
  const interno = it.agent_layer === "interno";
  const stage = it.person?.stage ? (STAGE_LABEL[it.person.stage] || it.person.stage) : null;
  const showTel = !!it.person?.name && it.telefono && !/^public-/i.test(it.telefono) && !/^(\d)\1{6,}$/.test(it.telefono);
  const puedeTomar = TAKEOVER_CHANNELS.has(String(it.channel)) && it.party !== "internal";

  return (
    <li
      className={[
        "group rounded-2xl border bg-card text-card-foreground p-3 sm:p-3.5 transition-shadow hover:shadow-md relative",
        urgent ? "border-rose-500/50 ring-1 ring-rose-500/30" : "border-border",
      ].join(" ")}
    >
      {urgent && <span aria-hidden className="absolute left-0 top-3 bottom-3 w-1 rounded-r bg-rose-500" />}
      <div className="flex items-start gap-3">
        <button onClick={onOpen} className="shrink-0 w-10 h-10 rounded-full bg-muted border border-border grid place-items-center text-[13px] font-semibold text-foreground" aria-label={`Abrir conversación con ${name}`}>
          {initials(name)}
        </button>
        <div className="flex-1 min-w-0">
          <button onClick={onOpen} className="w-full text-left">
            <div className="flex items-center gap-2 min-w-0">
              <span className="font-semibold text-[15px] truncate">{name}</span>
              {urgent && <span className="text-[11px] font-semibold text-rose-700 dark:text-rose-300 shrink-0">URGENTE</span>}
              <time className="ml-auto text-[12px] text-muted-foreground shrink-0 tabular-nums">{mxStamp(it.last_message_at)}</time>
            </div>
            <div className="mt-1 flex flex-wrap items-center gap-1.5">
              <span className={`text-[11px] px-1.5 py-0.5 rounded-md border font-medium inline-flex items-center gap-1 ${ch.cls}`}>
                <ch.Icon className="w-3 h-3" aria-hidden /> {ch.label}
              </span>
              {it.party !== "test" && (
                <span className={`text-[11px] px-1.5 py-0.5 rounded-md border inline-flex items-center gap-1 ${interno ? "border-primary/40 text-primary bg-primary/5" : "border-border text-foreground bg-muted"}`}>
                  <Bot className="w-3 h-3" aria-hidden /> {interno ? "Interno" : "Comercial"}
                </span>
              )}
              {it.party === "test" && <span className="text-[11px] px-1.5 py-0.5 rounded-md border border-border text-muted-foreground">Prueba</span>}
              {stage && <span className="text-[11px] px-1.5 py-0.5 rounded-md border border-border text-foreground">{stage}</span>}
              {showTel && <span className="text-[12px] text-muted-foreground tabular-nums">{it.telefono}</span>}
              {!!it.unread_admin_count && <span className="text-[11px] px-1.5 py-0.5 rounded-md bg-primary text-primary-foreground">{it.unread_admin_count} nuevos</span>}
            </div>
            {preview && <p className="mt-1.5 text-[13px] text-muted-foreground line-clamp-2 break-words">{preview}</p>}
          </button>
          {(puedeTomar || leadHref) && (
            <div className="mt-2 flex flex-wrap items-center gap-2">
              {puedeTomar && (
                <AgentControl
                  conv={{ thread_id: it.id, session_id: it.session_id, telefono: it.telefono }}
                  paused={!!it.agent_paused}
                  pausedUntil={it.agent_paused_until ?? null}
                  indefinite={!!it.agent_paused_indefinite}
                />
              )}
              {leadHref && (
                <a
                  href={leadHref}
                  onClick={(e) => { e.preventDefault(); onNavigate(leadHref); }}
                  className="text-[12px] inline-flex items-center gap-1 px-2 py-1 rounded-lg border border-border text-foreground hover:bg-muted"
                >
                  <ExternalLink className="w-3.5 h-3.5" aria-hidden /> Ficha del lead
                </a>
              )}
            </div>
          )}
        </div>
      </div>
    </li>
  );
}
